import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
} from "recharts";
import { fmtUsd, fmtInt, PROVIDER_LABEL, type AiUsageStats as Stats } from "./aiUsageFormat";

export const ProviderTable = ({ data }: { data: Stats }) => {
  const providers = Array.from(
    new Set([
      ...Object.keys(data.cost_by_provider_30d),
      ...Object.keys(data.tokens_in_by_provider_30d),
      ...Object.keys(data.tokens_out_by_provider_30d),
    ]),
  ).sort();

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Last 30 days</span>
          <span className="text-xs font-normal text-muted-foreground">
            {fmtInt(data.total_events_30d)} events · {data.failover_count_30d} failovers
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {providers.length === 0 ? (
          <p className="text-muted-foreground">No AI usage in the last 30 days.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b">
                  <th className="py-2 pr-4 font-medium">Provider</th>
                  <th className="py-2 pr-4 font-medium">Cost (30d)</th>
                  <th className="py-2 pr-4 font-medium">Tokens in</th>
                  <th className="py-2 pr-4 font-medium">Tokens out</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((p) => (
                  <tr key={p} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{PROVIDER_LABEL[p] ?? p}</td>
                    <td className="py-2 pr-4">{fmtUsd(data.cost_by_provider_30d[p] ?? 0)}</td>
                    <td className="py-2 pr-4">{fmtInt(data.tokens_in_by_provider_30d[p] ?? 0)}</td>
                    <td className="py-2 pr-4">{fmtInt(data.tokens_out_by_provider_30d[p] ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const DailySpendChart = ({ data }: { data: Stats }) => {
  const dailyCeiling =
    data.monthly_admin_budget_total_usd > 0 ? data.monthly_admin_budget_total_usd / 30 : 0;
  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Daily admin-pool spend</span>
          <span className="text-xs font-normal text-muted-foreground">
            Daily run-rate ceiling: {fmtUsd(dailyCeiling)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data.daily_admin_spend_30d}>
              <defs>
                <linearGradient id="adminSpendGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickFormatter={(d: string) => d.slice(5)}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 10 }}
                tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                width={48}
              />
              <Tooltip
                formatter={(v: number) => fmtUsd(v)}
                labelFormatter={(l: string) => l}
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 6,
                  fontSize: 12,
                }}
              />
              {dailyCeiling > 0 && (
                <ReferenceLine
                  y={dailyCeiling}
                  stroke="hsl(var(--destructive))"
                  strokeDasharray="3 3"
                  strokeOpacity={0.6}
                />
              )}
              <Area
                type="monotone"
                dataKey="cost_usd"
                stroke="hsl(var(--primary))"
                fill="url(#adminSpendGradient)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export const BudgetCard = ({ data }: { data: Stats }) => {
  const budgetPct =
    data.monthly_admin_budget_total_usd > 0
      ? Math.min(100, (data.admin_spend_this_month_total_usd / data.monthly_admin_budget_total_usd) * 100)
      : 0;
  const budgetDanger = budgetPct >= 80;
  const projectedPct =
    data.monthly_admin_budget_total_usd > 0
      ? (data.projected_month_end_admin_spend_usd / data.monthly_admin_budget_total_usd) * 100
      : 0;
  const projectionDanger = projectedPct > 100;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Global admin-pool budget (this calendar month)</span>
          <span className="text-xs font-normal text-muted-foreground">
            {fmtUsd(data.admin_spend_this_month_total_usd)} /{" "}
            {fmtUsd(data.monthly_admin_budget_total_usd)}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full ${budgetDanger ? "bg-destructive" : "bg-primary"}`}
            style={{ width: `${budgetPct}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {budgetPct.toFixed(1)}% of the global circuit-breaker budget consumed. When exceeded,
          all non-BYOK premium writing falls back to the default gateway.
        </p>
        <p
          className={`text-xs font-medium ${projectionDanger ? "text-destructive" : "text-foreground"}`}
        >
          Projected month-end spend at current pace: {fmtUsd(data.projected_month_end_admin_spend_usd)}
          <span className="ml-1 font-normal text-muted-foreground">
            ({projectedPct.toFixed(0)}% of budget)
          </span>
        </p>
      </CardContent>
    </Card>
  );
};

export const TopWorkspacesCard = ({ data }: { data: Stats }) => (
  <Card className="shadow-soft">
    <CardHeader>
      <CardTitle className="flex items-center justify-between">
        <span>Top admin-funded spenders this calendar month</span>
        <span className="text-xs font-normal text-muted-foreground">
          Total: {fmtUsd(data.admin_spend_this_month_total_usd)}
        </span>
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm">
      {data.top_admin_funded_workspaces.length === 0 ? (
        <p className="text-muted-foreground">No admin-funded usage this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b">
                <th className="py-2 pr-4 font-medium">Workspace</th>
                <th className="py-2 pr-4 font-medium">Tier</th>
                <th className="py-2 pr-4 font-medium">Spend (this month)</th>
              </tr>
            </thead>
            <tbody>
              {data.top_admin_funded_workspaces.map((row) => (
                <tr key={row.workspace_id} className="border-b last:border-b-0">
                  <td className="py-2 pr-4">
                    <div className="font-medium">{row.workspace_label ?? "(unnamed)"}</div>
                    <div className="text-xs text-muted-foreground">{row.workspace_id}</div>
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant="outline">{row.account_tier ?? "—"}</Badge>
                  </td>
                  <td className="py-2 pr-4">{fmtUsd(row.admin_spend_this_month_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);

export const TopConsumersCard = ({ data }: { data: Stats }) => (
  <Card className="shadow-soft">
    <CardHeader>
      <CardTitle>Top credit consumers this month</CardTitle>
    </CardHeader>
    <CardContent className="text-sm">
      {data.top_credit_consumers.length === 0 ? (
        <p className="text-muted-foreground">No credit usage this month.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b">
                <th className="py-2 pr-4 font-medium">Account</th>
                <th className="py-2 pr-4 font-medium">Tier</th>
                <th className="py-2 pr-4 font-medium">Workspaces</th>
                <th className="py-2 pr-4 font-medium">Credits used</th>
                <th className="py-2 pr-4 font-medium">Top-up balance</th>
              </tr>
            </thead>
            <tbody>
              {data.top_credit_consumers.map((row) => (
                <tr key={row.owner_user_id} className="border-b last:border-b-0">
                  <td className="py-2 pr-4">
                    <div className="font-medium">{row.display_name}</div>
                    <div className="text-xs text-muted-foreground">{row.owner_user_id}</div>
                  </td>
                  <td className="py-2 pr-4">
                    <Badge variant="outline">{row.account_tier}</Badge>
                  </td>
                  <td className="py-2 pr-4">{fmtInt(row.workspace_count)}</td>
                  <td className="py-2 pr-4">{fmtInt(row.credits_used_this_month)}</td>
                  <td className="py-2 pr-4">{fmtInt(row.topup_balance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);
